import { useEffect, useRef } from 'react';
import type { Customer } from '../types';

/**
 * Recovers keyboard focus when a card that had focus is removed from a stage
 * (filter change, optimistic mutation, collab update). Without this, the
 * browser drops focus to document.body and keyboard users lose their place.
 *
 * Scoping rules:
 * 1. Only acts when a customer was actually removed (identity-based, not
 *    count-based — handles the one-removed-one-added case).
 * 2. Only recovers if focus was inside THIS stage before the change. Prevents
 *    multiple stages from fighting over focus during a batch filter.
 * 3. Only recovers if focus is now orphaned (on body or detached). Won't
 *    steal focus from other interactive controls.
 *
 * Focus-tracking note: uses focusout's relatedTarget === null as the signal
 * for "focus lost due to element removal" — which is what we want to ignore,
 * so the ref stays "true" through the commit and the recovery effect can run.
 */
export function useStageFocusRecovery(customers: Customer[], containerId: string) {
  const prevIdsRef = useRef<Set<number>>(new Set(customers.map((c) => c.id)));
  const focusInsideRef = useRef(false);

  // Track whether focus is currently inside this stage's container.
  useEffect(() => {
    const stageEl = document.getElementById(containerId);
    if (!stageEl) return;

    // Initialize in case focus was already inside when the hook mounted.
    if (stageEl.contains(document.activeElement)) {
      focusInsideRef.current = true;
    }

    const onFocusIn = () => {
      focusInsideRef.current = true;
    };
    const onFocusOut = (e: FocusEvent) => {
      // relatedTarget === null means focus was lost to nothing (usually because
      // the focused element was removed from the DOM). In that case, keep the
      // ref "true" so the recovery effect below can act.
      if (e.relatedTarget === null) return;
      if (!stageEl.contains(e.relatedTarget as Node)) {
        focusInsideRef.current = false;
      }
    };

    stageEl.addEventListener('focusin', onFocusIn);
    stageEl.addEventListener('focusout', onFocusOut);
    return () => {
      stageEl.removeEventListener('focusin', onFocusIn);
      stageEl.removeEventListener('focusout', onFocusOut);
    };
  }, [containerId]);

  // Recover focus when cards are removed from this stage.
  useEffect(() => {
    const currentIds = new Set(customers.map((c) => c.id));
    const prevIds = prevIdsRef.current;

    let removed = false;
    for (const id of prevIds) {
      if (!currentIds.has(id)) {
        removed = true;
        break;
      }
    }
    prevIdsRef.current = currentIds;

    if (!removed) return;
    if (!focusInsideRef.current) return;

    const active = document.activeElement;
    const orphaned = active === document.body || !document.body.contains(active);
    if (!orphaned) return;

    const stageEl = document.getElementById(containerId);
    if (stageEl) {
      stageEl.focus({ preventScroll: true });
      // Focus is now on the container itself, which is still "inside" the stage.
      focusInsideRef.current = true;
    }
  }, [customers, containerId]);
}
